import { Events } from "@Context/common/general/context";
import AccountRoles from "@Enums/AccountRoles";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { formatName } from "@Helpers/formatters";
import { useApi } from "@hooks";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useI18n from "@i18n";
import { Api } from "@Shared/schemas";
import { AccountUserRecord, GetAccountResponse, RoleRecord } from "@Shared/schemas/accounts/accountSchemas";
import { InviteResponse } from "@Shared/schemas/accounts/inviteSchemas";
import { UserRecord } from "@Shared/schemas/users/userSchemas";
import { getRoleLevel } from "@Shared/utils/rolePermissions";
import { useCallback, useEffect, useMemo, useState } from "react";

interface IPendingMember {
  id?: number | null;
  email: string;
  role: RoleRecord;
  isPending: true;
  invite: InviteResponse;
  first_name?: string;
  last_name?: string;
  rating?: number;
  avatar?: string | null;
  doc_id?: string;
}

interface IActiveMember extends UserRecord {
  account_user: AccountUserRecord;
  role: RoleRecord;
  isPending: false;
}

export type MemberOrInvite = IActiveMember | IPendingMember;
export type MembersActiveFilter = "all" | "members" | "invites";
export type MembersSortMode = "default" | "name" | "role" | "email";
export type MembersSortDirection = "asc" | "desc";

export const useMembersLogic = () => {
  const { i18n } = useI18n();
  const { globalState } = useGlobalContext();
  const [members, setMembers] = useState<MemberOrInvite[]>([]);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [allRoles, setAllRoles] = useState<RoleRecord[]>([]);
  const [hasResolvedRoles, setHasResolvedRoles] = useState(false);
  const userId = globalState.user?.dbId;
  const accountId = globalState.account?.id;
  const userRole = globalState.account?.user_role;
  const [activeFilter, setActiveFilter] = useState<MembersActiveFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [sortMode, setSortMode] = useState<MembersSortMode>("default");
  const [sortDirection, setSortDirection] = useState<MembersSortDirection>("asc");
  const [selectedPendingInvite, setSelectedPendingInvite] = useState<IPendingMember | null>(null);
  const [updatingMemberId, setUpdatingMemberId] = useState<number | null>(null);

  const currentUserRoleLevel = userRole ? getRoleLevel(userRole) : null;
  const isAdmin = Boolean(currentUserRoleLevel !== null && currentUserRoleLevel >= getRoleLevel(AccountRoles.ADMIN));
  const isAccountRoleResolving = Boolean(globalState.user.authenticated && accountId && !userRole);

  const { call: getRoles, loading: getRolesLoading } = useApi({
    api: Api.Account.GetRoles,
    request: {},
  });

  const { call: getInvites, loading: getInvitesLoading } = useApi({
    api: Api.Invites.GetInvitesForAccount,
    request: {
      account_id: accountId!,
    },
  });

  const { call: deleteInvite, loading: isDeleteInviteLoading } = useApi({
    api: Api.Invites.DeleteInvite,
    request: {} as any,
  });

  const { call: createInvites, loading: isCreateInvitesLoading } = useApi({
    api: Api.Invites.CreateInvites,
    request: {} as any,
  });

  const { call: updateMember, loading: isUpdateMemberLoading } = useApi({
    api: Api.Account.UpdateMember,
    request: {} as any,
  });

  const processMembers = useCallback(
    async (account: GetAccountResponse, currentRoles: RoleRecord[]) => {
      const activeMembers = account.members
        .map((member: AccountUserRecord): IActiveMember | null => {
          const userRecord = account.member_records?.find((record: UserRecord) => record.id === member.user_id);
          if (!userRecord) return null;
          const memberRole = currentRoles.find(role => role.id === member.role_id);
          if (!memberRole) return null;
          return {
            ...userRecord,
            account_user: member,
            role: memberRole,
            isPending: false,
          };
        })
        .filter((member): member is IActiveMember => member !== null)
        .sort((a, b) => {
          const aLevel = getRoleLevel(a.role.name as AccountRoles);
          const bLevel = getRoleLevel(b.role.name as AccountRoles);
          return bLevel - aLevel;
        });

      const membersOrInvites: MemberOrInvite[] = [...activeMembers];

      if (isAdmin && accountId) {
        const { data: invites } = await getInvites({ account_id: accountId });
        const pendingMembers: IPendingMember[] = (invites || [])
          .filter((invite: InviteResponse) => invite.state === "pending")
          .map((invite: InviteResponse): IPendingMember | null => {
            const inviteRole = currentRoles.find((role) => role.id === invite.role_id);
            if (!inviteRole) return null;
            return {
              id: invite.recipient_user_id ?? null,
              email: invite.email,
              role: inviteRole,
              isPending: true,
              invite,
            };
          })
          .filter((member): member is IPendingMember => member !== null);

        membersOrInvites.push(...pendingMembers);
      }

      setMembers(membersOrInvites);
    },
    [accountId, getInvites, isAdmin]
  );

  const { call: getAccounts, loading: getAccountsLoading } = useApi({
    api: Api.Account.GetAccounts,
    request: {
      resolve_members: true,
      account_ids: accountId ? [accountId] : undefined,
    },
  });

  const refreshMembers = useCallback(async () => {
    if (!accountId || !hasResolvedRoles || allRoles.length === 0) {
      if (!accountId) {
        setMembers([]);
      }
      return;
    }

    const response = await getAccounts({
      resolve_members: true,
      account_ids: [accountId],
    });

    if (response.data?.[0]) {
      await processMembers(response.data[0], allRoles);
    } else {
      setMembers([]);
    }
  }, [accountId, allRoles, getAccounts, hasResolvedRoles, processMembers]);

  const loadRoles = useCallback(async () => {
    if (!userRole) {
      setRoles([]);
      setAllRoles([]);
      setHasResolvedRoles(false);
      return;
    }

    const { data } = await getRoles({});
    const fetchedRoles = data || [];
    const availableRoles = fetchedRoles
      .filter((role) => {
        const roleEnum = role.name as AccountRoles;
        return getRoleLevel(roleEnum) < getRoleLevel(userRole);
      })
      .sort((a, b) => {
        const aLevel = getRoleLevel(a.name as AccountRoles);
        const bLevel = getRoleLevel(b.name as AccountRoles);
        return bLevel - aLevel;
      });

    setRoles(availableRoles);
    setAllRoles(fetchedRoles);
    setSelectedRoleIds(fetchedRoles.map((role) => String(role.id)));
    setHasResolvedRoles(true);
  }, [getRoles, userRole]);

  const refreshAccountAndMembers = useCallback(async () => {
    await refreshMembers();
    Events.account.refreshCurrentAccount.emit();
  }, [refreshMembers]);

  const handleNavigateToMember = (id: number) => {
    Events.app.navigate.emit({ path: `/settings/members/${id}` });
  };

  const handleInviteMember = () => {
    Events.app.navigate.emit({ path: "/settings/members/invite" });
  };

  const handleDeleteInvite = async (inviteId: number) => {
    await deleteInvite({
      invite_id: inviteId,
    });
    setSelectedPendingInvite(null);
    await refreshAccountAndMembers();
  };

  const handleResendInvite = async (member: IPendingMember) => {
    const response = await createInvites({
      account_id: accountId!,
      invites: [
        {
          email: member.email,
          role_id: member.role.id,
          recipient_user_id: member.invite.recipient_user_id || undefined,
        },
      ],
    });

    setSelectedPendingInvite(null);
    await refreshAccountAndMembers();
    return response;
  };

  const handleUpdateMemberRole = async (member: MemberOrInvite, roleId: number) => {
    if (member.isPending || !member.id || !accountId) {
      return;
    }

    setUpdatingMemberId(member.id);
    try {
      await updateMember({
        action: "update",
        account_id: accountId,
        user_id: member.id,
        role_id: roleId,
      });
      await refreshAccountAndMembers();
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleRemoveMember = async (member: MemberOrInvite) => {
    if (!member.isPending && member.id && accountId) {
      setUpdatingMemberId(member.id);
      try {
        await updateMember({
          action: "remove",
          account_id: accountId,
          user_id: member.id,
          role_id: member.role.id,
        });
        await refreshAccountAndMembers();
      } finally {
        setUpdatingMemberId(null);
      }
    }
  };

  const filteredMembersSearchAndRoles = useMemo(() => {
    const normalizedSearch = searchInput.trim().toLowerCase();
    const filteredMembers = members.filter(member => {
      const memberName = member.isPending ? i18n("settings.members.pending") : formatName(member.first_name, member.last_name);
      const memberEmail = member.email || "";

      if (!selectedRoleIds.includes(String(member.role.id))) return false;
      if (!normalizedSearch) return true;

      const searchableValues = [memberEmail, member.role.name, memberName, member.first_name, member.last_name];

      return searchableValues.filter(Boolean).some(value => String(value).toLowerCase().includes(normalizedSearch));
    });

    return filteredMembers;
  }, [activeFilter, i18n, members, searchInput, selectedRoleIds]);

  const filteredAndSortedMembers = useMemo(() => {
    const nextMembers = filteredMembersSearchAndRoles.filter(member => {
      if (activeFilter === "invites" && !member.isPending) return false;
      if (activeFilter === "members" && member.isPending) return false;
      return true;
    });

    const directionMultiplier = sortDirection === "asc" ? 1 : -1;

    if (sortMode === "name") {
      return [...nextMembers].sort(
        (a, b) =>
          directionMultiplier *
          (a.isPending ? a.email : formatName(a.first_name, a.last_name)).localeCompare(b.isPending ? b.email : formatName(b.first_name, b.last_name))
      );
    }

    if (sortMode === "role") {
      return [...nextMembers].sort((a, b) => directionMultiplier * a.role.name.localeCompare(b.role.name));
    }

    if (sortMode === "email") {
      return [...nextMembers].sort((a, b) => directionMultiplier * (a.email || "").localeCompare(b.email || ""));
    }

    return nextMembers;
  }, [activeFilter, i18n, filteredMembersSearchAndRoles, searchInput, selectedRoleIds, sortDirection, sortMode]);

  const memberFilterCounts = useMemo(
    () => ({
      all: filteredMembersSearchAndRoles.length,
      members: filteredMembersSearchAndRoles.filter(member => !member.isPending).length,
      invites: filteredMembersSearchAndRoles.filter(member => member.isPending).length,
    }),
    [filteredMembersSearchAndRoles]
  );

  useEffect(() => {
    Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: i18n("settings.members.title") });
    return () => Events.app.hideHeaderButton.emit();
  }, [i18n]);

  useEffect(() => {
    if (!userRole) {
      setMembers([]);
      setRoles([]);
      setAllRoles([]);
      setHasResolvedRoles(false);
      return;
    }

    loadRoles();
  }, [loadRoles, userRole]);

  useEffect(() => {
    if (accountId && hasResolvedRoles && allRoles.length > 0) {
      refreshMembers();
    }
  }, [accountId, allRoles.length, hasResolvedRoles, refreshMembers]);

  const isLoading =
    isAccountRoleResolving ||
    getAccountsLoading ||
    getRolesLoading ||
    getInvitesLoading ||
    isDeleteInviteLoading ||
    isCreateInvitesLoading ||
    isUpdateMemberLoading;

  return {
    filteredMembers: filteredAndSortedMembers,
    isLoading,
    userId,
    globalState,
    roles,
    allRoles,
    activeFilter,
    searchInput,
    selectedRoleIds,
    sortMode,
    sortDirection,
    memberFilterCounts,
    updatingMemberId,
    isDeleteInviteLoading,
    isCreateInvitesLoading,
    isUpdateMemberLoading,
    selectedPendingInvite,
    setActiveFilter,
    setSearchInput,
    setSelectedRoleIds,
    setSortMode,
    setSortDirection,
    setSelectedPendingInvite,
    handleNavigateToMember,
    handleInviteMember,
    handleDeleteInvite,
    handleRemoveMember,
    handleResendInvite,
    handleUpdateMemberRole,
    refreshMembers,
  };
};
